<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Widget from 'dashboard/modules/widget-preview/components/Widget.vue';
import InputRadioGroup from './components/InputRadioGroup.vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { normalizeHttpUrl } from 'shared/helpers/URLHelper';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';

export default {
  components: {
    Widget,
    InputRadioGroup,
    NextButton,
    Editor,
    Avatar,
  },
  props: {
    inbox: {
      type: Object,
      default: () => {},
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      isWidgetPreview: true,
      color: '#1f93ff',
      accentColor: '#1f93ff',
      websiteName: '',
      welcomeHeading: '',
      welcomeTagline: '',
      offlineMessage: '',
      replyTime: 'in_a_few_minutes',
      customReplyTimeText: '',
      avatarFile: null,
      avatarUrl: '',
      closeButtonAvatarFile: null,
      closeButtonAvatarUrl: '',
      widgetBubblePosition: 'right',
      widgetBubbleLauncherTitle: this.$t(
        'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_LAUNCHER_TITLE.DEFAULT'
      ),
      widgetBubbleType: 'standard',
      widgetTemplate: 'Template1',
      widgetFontFamily: 'Inter',
      expandableHeight: false,
      expandableWidth: false,
      widgetRedirectUrlName: '',
      widgetRedirectUrlLink: '',
      widgetRedirectUrlEnabled: true,
      displayBranding: true,
      displayDisclaimer: true,
      disclaimerMessage: '',
      brandingText: '',
      brandingLink: '',
      brandingImageUrl: '',
      widgetBubblePositions: [
        {
          id: 'left',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION.LEFT'
          ),
          checked: false,
        },
        {
          id: 'right',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION.RIGHT'
          ),
          checked: true,
        },
      ],
      widgetBubbleTypes: [
        {
          id: 'standard',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE.STANDARD'
          ),
          checked: true,
        },
        {
          id: 'expanded_bubble',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE.EXPANDED_BUBBLE'
          ),
          checked: false,
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    storageKey() {
      return `${LOCAL_STORAGE_KEYS.WIDGET_BUILDER}${this.inbox.id}`;
    },
    widgetScript() {
      let options = {
        position: this.widgetBubblePosition,
        type: this.widgetBubbleType,
        launcherTitle: this.widgetBubbleLauncherTitle,
      };
      let script = this.inbox.web_widget_script;
      return (
        script.substring(0, 13) +
        this.$t('INBOX_MGMT.WIDGET_BUILDER.SCRIPT_SETTINGS', {
          options: JSON.stringify(options),
        }) +
        script.substring(13)
      );
    },
    getWidgetViewOptions() {
      return [
        {
          id: 'preview',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_VIEW_OPTION.PREVIEW'
          ),
          checked: true,
        },
        {
          id: 'script',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_VIEW_OPTION.SCRIPT'
          ),
          checked: false,
        },
      ];
    },
    getReplyTimeOptions() {
      return [
        {
          key: 'in_a_few_minutes',
          value: 'in_a_few_minutes',
          text: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REPLY_TIME.IN_A_FEW_MINUTES'
          ),
        },
        {
          key: 'in_a_few_hours',
          value: 'in_a_few_hours',
          text: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REPLY_TIME.IN_A_FEW_HOURS'
          ),
        },
        {
          key: 'in_a_day',
          value: 'in_a_day',
          text: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REPLY_TIME.IN_A_DAY'
          ),
        },
        {
          key: 'custom',
          value: 'custom',
          text: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REPLY_TIME.CUSTOM'
          ),
        },
      ];
    },
    getWidgetTemplateOptions() {
      return [
        {
          key: 'Template1',
          value: 'Template1',
          text: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_TEMPLATE.TEMPLATE_1'
          ),
        },
        {
          key: 'Template2',
          value: 'Template2',
          text: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_TEMPLATE.TEMPLATE_2'
          ),
        },
        {
          key: 'Template3',
          value: 'Template3',
          text: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_TEMPLATE.TEMPLATE_3'
          ),
        },
      ];
    },
    getFontFamilyOptions() {
      return [
        {
          key: 'Inter',
          value: 'Inter',
          text: 'Inter',
        },
        {
          key: 'Lato',
          value: 'Lato',
          text: 'Lato',
        },
        {
          key: 'Open Sans',
          value: 'Open Sans',
          text: 'Open Sans',
        },
        {
          key: 'Manrope',
          value: 'Manrope',
          text: 'Manrope',
        },
        {
          key: 'Roboto',
          value: 'Roboto',
          text: 'Roboto',
        },
        {
          key: 'Montserrat',
          value: 'Montserrat',
          text: 'Montserrat',
        },
        {
          key: 'Poppins',
          value: 'Poppins',
          text: 'Poppins',
        },
        {
          key: 'Nunito',
          value: 'Nunito',
          text: 'Nunito',
        },
        {
          key: 'Source Sans',
          value: 'Source Sans',
          text: 'Source Sans',
        },
        {
          key: 'Raleway',
          value: 'Raleway',
          text: 'Raleway',
        },
      ];
    },
    websiteNameValidationErrorMsg() {
      return this.v$.websiteName.$error
        ? this.$t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WEBSITE_NAME.ERROR')
        : '';
    },
  },
  mounted() {
    this.setDefaults();
  },
  validations: {
    websiteName: { required },
  },
  methods: {
    setDefaults() {
      // Widget Settings
      const {
        name,
        welcome_title,
        welcome_tagline,
        offline_message,
        widget_color,
        widget_accent_color,
        reply_time,
        avatar_url,
        close_button_avatar_url,
      } = this.inbox;
      this.websiteName = name;
      // Both are nullable in the DB, and Editor/woot-input require a String
      // (a prop `default` only applies to undefined, not null).
      this.welcomeHeading = welcome_title || '';
      this.welcomeTagline = welcome_tagline || '';
      this.offlineMessage = offline_message || '';
      this.color = widget_color;
      this.accentColor = widget_accent_color || widget_color;
      this.replyTime = reply_time;
      this.avatarUrl = avatar_url;
      this.closeButtonAvatarUrl = close_button_avatar_url;
      this.widgetTemplate = this.inbox.widget_template || 'Template1';
      this.widgetFontFamily = this.inbox.widget_font_family || 'Inter';
      this.widgetRedirectUrlName = this.inbox.widget_redirect_url_name || '';
      this.widgetRedirectUrlLink = this.inbox.widget_redirect_url_link || '';
      this.widgetRedirectUrlEnabled =
        this.inbox.widget_redirect_url_enabled !== false;
      this.expandableHeight = this.inbox.expandable_height || false;
      this.expandableWidth = this.inbox.expandable_width || false;
      this.displayBranding = this.inbox.display_branding !== false;
      this.displayDisclaimer = this.inbox.display_disclaimer !== false;
      this.disclaimerMessage = this.inbox.disclaimer_message || '';
      this.brandingText = this.inbox.branding_text || '';
      this.brandingLink = this.inbox.branding_link || '';
      this.brandingImageUrl = this.inbox.branding_image_url || '';

      // Check if reply_time is custom text (not a predefined option)
      const predefinedOptions = [
        'in_a_few_minutes',
        'in_a_few_hours',
        'in_a_day',
      ];
      if (!predefinedOptions.includes(reply_time)) {
        this.replyTime = 'custom';
        this.customReplyTimeText = reply_time;
      }

      const savedInformation = this.getSavedInboxInformation();
      if (savedInformation) {
        this.widgetBubblePositions = this.widgetBubblePositions.map(item => {
          if (item.id === savedInformation.position) {
            item.checked = true;
            this.widgetBubblePosition = item.id;
          }
          return item;
        });
        this.widgetBubbleTypes = this.widgetBubbleTypes.map(item => {
          if (item.id === savedInformation.type) {
            item.checked = true;
            this.widgetBubbleType = item.id;
          }
          return item;
        });
        this.widgetBubbleLauncherTitle =
          savedInformation.launcherTitle || 'Chat with us';
      }
    },
    handleWidgetBubblePositionChange(item) {
      this.widgetBubblePosition = item.id;
    },
    handleWidgetBubbleTypeChange(item) {
      this.widgetBubbleType = item.id;
    },
    handleWidgetViewChange(item) {
      this.isWidgetPreview = item.id === 'preview';
    },
    handleImageUpload({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    handleCloseButtonAvatarUpload({ file, url }) {
      this.closeButtonAvatarFile = file;
      this.closeButtonAvatarUrl = url;
    },
    async handleAvatarDelete() {
      try {
        await this.$store.dispatch('inboxes/deleteInboxAvatar', this.inbox.id);
        this.avatarFile = null;
        this.avatarUrl = '';
        useAlert(
          this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.AVATAR.DELETE.API.SUCCESS_MESSAGE'
          )
        );
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t(
                'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.AVATAR.DELETE.API.ERROR_MESSAGE'
              )
        );
      }
    },
    async handleCloseButtonAvatarDelete() {
      try {
        await this.$store.dispatch(
          'inboxes/deleteInboxCloseButtonAvatar',
          this.inbox.id
        );
        this.closeButtonAvatarFile = null;
        this.closeButtonAvatarUrl = '';
        useAlert('Close button avatar deleted successfully');
      } catch (error) {
        useAlert(error.message || 'Failed to delete close button avatar');
      }
    },
    async updateWidget() {
      const bubbleSettings = {
        position: this.widgetBubblePosition,
        launcherTitle: this.widgetBubbleLauncherTitle,
        type: this.widgetBubbleType,
      };

      LocalStorage.set(this.storageKey, bubbleSettings);

      try {
        const payload = {
          id: this.inbox.id,
          name: this.websiteName,
          channel: {
            widget_color: this.color,
            widget_accent_color: this.accentColor,
            welcome_title: this.welcomeHeading,
            welcome_tagline: this.welcomeTagline,
            offline_message: this.offlineMessage,
            reply_time:
              this.replyTime === 'custom'
                ? this.customReplyTimeText
                : this.replyTime,
            widget_template: this.widgetTemplate,
            widget_font_family: this.widgetFontFamily,
            widget_redirect_url_name: this.widgetRedirectUrlName,
            widget_redirect_url_link: this.widgetRedirectUrlLink,
            widget_redirect_url_enabled: this.widgetRedirectUrlEnabled,
            expandable_height: this.expandableHeight,
            expandable_width: this.expandableWidth,
            display_branding: this.displayBranding,
            display_disclaimer: this.displayDisclaimer,
            disclaimer_message: this.disclaimerMessage,
            branding_text: this.brandingText,
            branding_link: normalizeHttpUrl(this.brandingLink),
            branding_image_url: normalizeHttpUrl(this.brandingImageUrl),
          },
        };
        if (this.avatarFile) {
          payload.avatar = this.avatarFile;
        }
        if (this.closeButtonAvatarFile) {
          payload.channel.close_button_avatar = this.closeButtonAvatarFile;
        }
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(
          this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.UPDATE.API.SUCCESS_MESSAGE'
          )
        );
      } catch (error) {
        useAlert(
          error.message ||
            this.$t(
              'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.UPDATE.API.ERROR_MESSAGE'
            )
        );
      }
    },
    getSavedInboxInformation() {
      return LocalStorage.get(this.storageKey);
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <div class="flex p-2.5">
      <div class="w-100 lg:w-[40%]">
        <div class="min-h-full py-4 overflow-y-scroll px-px">
          <form @submit.prevent="updateWidget">
            <div class="flex flex-col mb-4 items-start gap-1 w-full">
              <label class="mb-0.5 text-sm font-medium text-n-slate-12">
                {{
                  $t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.AVATAR.LABEL')
                }}
              </label>
              <Avatar
                :src="avatarUrl"
                :size="72"
                icon-name="i-ri-global-fill"
                name=""
                allow-upload
                rounded-full
                @upload="handleImageUpload"
                @delete="handleAvatarDelete"
              />
            </div>
            <div class="flex flex-col mb-4 items-start gap-1 w-full">
              <label class="mb-0.5 text-sm font-medium text-n-slate-12">
                {{
                  $t(
                    'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.CLOSE_BUTTON_AVATAR.LABEL'
                  )
                }}
              </label>
              <Avatar
                :src="closeButtonAvatarUrl"
                :size="72"
                icon-name="i-ri-global-fill"
                name=""
                allow-upload
                rounded-full
                @upload="handleCloseButtonAvatarUpload"
                @delete="handleCloseButtonAvatarDelete"
              />
            </div>
            <woot-input
              v-model="websiteName"
              :class="{ error: v$.websiteName.$error }"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WEBSITE_NAME.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WEBSITE_NAME.PLACE_HOLDER'
                )
              "
              :error="websiteNameValidationErrorMsg"
              @blur="v$.websiteName.$touch"
            />
            <woot-input
              v-model="welcomeHeading"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WELCOME_HEADING.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WELCOME_HEADING.PLACE_HOLDER'
                )
              "
            />
            <Editor
              v-model="welcomeTagline"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WELCOME_TAGLINE.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WELCOME_TAGLINE.PLACE_HOLDER'
                )
              "
              :max-length="255"
              channel-type="Context::InboxSettings"
              class="mb-4"
            />
            <woot-input
              v-model="offlineMessage"
              :label="
                $t(
                  'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_OFFLINE_MESSAGE.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_OFFLINE_MESSAGE.PLACEHOLDER'
                )
              "
            />
            <label>
              {{
                $t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REPLY_TIME.LABEL')
              }}
              <select v-model="replyTime">
                <option
                  v-for="option in getReplyTimeOptions"
                  :key="option.key"
                  :value="option.value"
                >
                  {{ option.text }}
                </option>
              </select>
            </label>
            <woot-input
              v-if="replyTime === 'custom'"
              v-model="customReplyTimeText"
              label="Custom Reply Time Text"
              placeholder="Enter custom reply time text"
            />
            <label>
              {{
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_TEMPLATE.LABEL'
                )
              }}
              <select v-model="widgetTemplate">
                <option
                  v-for="option in getWidgetTemplateOptions"
                  :key="option.key"
                  :value="option.value"
                >
                  {{ option.text }}
                </option>
              </select>
            </label>
            <woot-input
              v-if="
                widgetTemplate === 'Template2' || widgetTemplate === 'Template3'
              "
              v-model="widgetRedirectUrlName"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REDIRECT_URL_NAME.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REDIRECT_URL_NAME.PLACE_HOLDER'
                )
              "
            />
            <woot-input
              v-if="
                widgetTemplate === 'Template2' || widgetTemplate === 'Template3'
              "
              v-model="widgetRedirectUrlLink"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REDIRECT_URL_LINK.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REDIRECT_URL_LINK.PLACE_HOLDER'
                )
              "
            />
            <label v-if="widgetTemplate === 'Template2'">
              {{
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REDIRECT_URL_ENABLED.LABEL'
                )
              }}
              <select v-model="widgetRedirectUrlEnabled">
                <option :value="true">
                  {{
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REDIRECT_URL_ENABLED.ACTIVE'
                    )
                  }}
                </option>
                <option :value="false">
                  {{
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REDIRECT_URL_ENABLED.INACTIVE'
                    )
                  }}
                </option>
              </select>
            </label>
            <label>
              {{
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_FONT_FAMILY.LABEL'
                )
              }}
              <select v-model="widgetFontFamily">
                <option
                  v-for="option in getFontFamilyOptions"
                  :key="option.key"
                  :value="option.value"
                >
                  {{ option.text }}
                </option>
              </select>
            </label>
            <label>
              {{
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.EXPANDABLE_HEIGHT.LABEL'
                )
              }}
              <select v-model="expandableHeight">
                <option :value="true">
                  {{
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.EXPANDABLE_HEIGHT.ACTIVE'
                    )
                  }}
                </option>
                <option :value="false">
                  {{
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.EXPANDABLE_HEIGHT.INACTIVE'
                    )
                  }}
                </option>
              </select>
            </label>
            <label>
              {{
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.EXPANDABLE_WIDTH.LABEL'
                )
              }}
              <select v-model="expandableWidth">
                <option :value="true">
                  {{
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.EXPANDABLE_WIDTH.ACTIVE'
                    )
                  }}
                </option>
                <option :value="false">
                  {{
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.EXPANDABLE_WIDTH.INACTIVE'
                    )
                  }}
                </option>
              </select>
            </label>
            <label>
              {{
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.DISPLAY_BRANDING.LABEL'
                )
              }}
              <select v-model="displayBranding">
                <option :value="true">
                  {{
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.DISPLAY_BRANDING.ACTIVE'
                    )
                  }}
                </option>
                <option :value="false">
                  {{
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.DISPLAY_BRANDING.INACTIVE'
                    )
                  }}
                </option>
              </select>
            </label>
            <label>
              {{
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.DISPLAY_DISCLAIMER.LABEL'
                )
              }}
              <select v-model="displayDisclaimer">
                <option :value="true">
                  {{
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.DISPLAY_DISCLAIMER.ACTIVE'
                    )
                  }}
                </option>
                <option :value="false">
                  {{
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.DISPLAY_DISCLAIMER.INACTIVE'
                    )
                  }}
                </option>
              </select>
            </label>
            <woot-input
              v-if="displayDisclaimer"
              v-model="disclaimerMessage"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.DISCLAIMER_MESSAGE.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.DISCLAIMER_MESSAGE.PLACE_HOLDER'
                )
              "
            />
            <woot-input
              v-if="displayBranding"
              v-model="brandingText"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.BRANDING_TEXT.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.BRANDING_TEXT.PLACE_HOLDER'
                )
              "
            />
            <woot-input
              v-if="displayBranding"
              v-model="brandingLink"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.BRANDING_LINK.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.BRANDING_LINK.PLACE_HOLDER'
                )
              "
            />
            <woot-input
              v-if="displayBranding"
              v-model="brandingImageUrl"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.BRANDING_IMAGE_URL.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.BRANDING_IMAGE_URL.PLACE_HOLDER'
                )
              "
            />
            <label>
              {{
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_COLOR_LABEL'
                )
              }}
              <woot-color-picker v-model="color" />
            </label>
            <label>
              {{
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_ACCENT_COLOR_LABEL'
                )
              }}
              <woot-color-picker v-model="accentColor" />
            </label>
            <InputRadioGroup
              name="widget-bubble-position"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION_LABEL'
                )
              "
              :items="widgetBubblePositions"
              :action="handleWidgetBubblePositionChange"
            />
            <InputRadioGroup
              name="widget-bubble-type"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE_LABEL'
                )
              "
              :items="widgetBubbleTypes"
              :action="handleWidgetBubbleTypeChange"
            />
            <woot-input
              v-model="widgetBubbleLauncherTitle"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_LAUNCHER_TITLE.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_LAUNCHER_TITLE.PLACE_HOLDER'
                )
              "
            />
            <NextButton
              type="submit"
              class="mt-4"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.UPDATE.BUTTON_TEXT'
                )
              "
              :is-loading="uiFlags.isUpdating"
              :disabled="v$.$invalid || uiFlags.isUpdating"
            />
          </form>
        </div>
      </div>
      <div class="w-100 lg:w-3/5">
        <InputRadioGroup
          name="widget-view-options"
          class="text-center"
          :items="getWidgetViewOptions"
          :action="handleWidgetViewChange"
        />
        <div
          v-if="isWidgetPreview"
          class="flex flex-col items-center justify-end min-h-[40.625rem] mx-5 mb-5 p-2.5 bg-n-slate-3 rounded-lg"
        >
          <Widget
            :welcome-heading="welcomeHeading"
            :welcome-tagline="welcomeTagline"
            :website-name="websiteName"
            :logo="avatarUrl"
            is-online
            :reply-time="replyTime"
            :color="color"
            :widget-bubble-position="widgetBubblePosition"
            :widget-bubble-launcher-title="widgetBubbleLauncherTitle"
            :widget-bubble-type="widgetBubbleType"
            :widget-template="widgetTemplate"
            :widget-font-family="widgetFontFamily"
            :widget-redirect-url-name="widgetRedirectUrlName"
            :widget-redirect-url-link="widgetRedirectUrlLink"
            :widget-redirect-url-enabled="widgetRedirectUrlEnabled"
            :display-branding="displayBranding"
            :display-disclaimer="displayDisclaimer"
            :disclaimer-message="disclaimerMessage"
            :branding-text="brandingText"
            :branding-link="brandingLink"
            :branding-image-url="brandingImageUrl"
            :close-button-avatar-url="closeButtonAvatarUrl"
          />
        </div>
        <div
          v-else
          class="mx-5 p-2.5 bg-n-slate-3 rounded-lg dark:bg-n-solid-3"
        >
          <woot-code :script="widgetScript" />
        </div>
      </div>
    </div>
  </div>
</template>
